// WP4 §5 submission pipeline — client side.
// Every input surface (A1 weighting, A2 map mission, A3O official,
// A3C community) submits through the same endpoint pattern with its own
// schema identifier. The server file is the system of record; email is
// the notification copy. Retries are safe: the record_id (a client UUID)
// is the idempotency key, so a resend can never create a second row or
// a second email.

import type {
  A2Record,
  A3CommunityDraft,
  A3OfficialDraft,
  Stage2State,
} from "./types";
import type { A1Draft } from "../types";

export const SUBMISSION_SCHEMAS = {
  A1: "A1-weighting-1.0",
  A2: "A2-mission-1.0",
  A3O: "A3O-official-1.0",
  A3C: "A3C-community-1.0",
} as const;

/** WP5-14: private contributor identification carried on every record. */
export interface ContributorBlock {
  name: string;
  id: string;
  organisation: string;
}

export interface DeviceBlock {
  type: "computer" | "cellphone" | "tablet";
  id: string;
}

export interface SubmissionEnvelope {
  schema_id: string;
  record_id: string;
  submitted_at_client: string;
  contact_phone: string;
  app_version: string;
  contributor: ContributorBlock;
  device: DeviceBlock;
  /** Area / town / settlement where the questionnaire was completed. */
  completion_area: string;
  /** "lat,lng" when the contributor agreed to share it; otherwise "". */
  completion_gps: string;
  payload: unknown;
  hp_field: string;
}

export interface SubmissionReceipt {
  record_id: string;
  received_at: string;
  duplicate: boolean;
}

export type PhoneCheck =
  | { ok: true; normalised: string }
  | { ok: false; message: string };

/**
 * South African phone gate (WP4 §5.1.2), mirrored server-side.
 * Accepts 0XXXXXXXXX (10 digits) or +27/27 followed by 9 digits;
 * normalises to +27XXXXXXXXX.
 */
export function validateSAPhone(raw: string): PhoneCheck {
  const digits = raw.replace(/[\s\-().]/g, "");
  if (!digits) {
    return {
      ok: false,
      message: "A South African contact number is required before sending.",
    };
  }
  let national: string | null = null;
  if (/^0\d{9}$/.test(digits)) {
    national = digits.slice(1);
  } else if (/^\+27\d{9}$/.test(digits)) {
    national = digits.slice(3);
  } else if (/^27\d{9}$/.test(digits)) {
    national = digits.slice(2);
  }
  if (!national || /^0/.test(national)) {
    return {
      ok: false,
      message:
        "Use a South African format: 0XX XXX XXXX or +27 XX XXX XXXX. Please correct the phone number.",
    };
  }
  return { ok: true, normalised: `+27${national}` };
}

/** WP5-18: the live server endpoint, used whenever the portal is NOT being
 *  served from the hosting itself (local station via START_STUDENT_TOOL.cmd,
 *  file copy, localhost preview). Same-origin PHP is preferred when present
 *  so a future different hosting path keeps working without a rebuild. */
const LIVE_ENDPOINT = "https://otleng.co.za/Student_support/submit_student_wsi.php";

export function submissionEndpoint(): string {
  const override = import.meta.env.VITE_SUBMIT_ENDPOINT as string | undefined;
  if (override) return override;
  const { protocol, hostname } = window.location;
  const localStation =
    protocol === "file:" ||
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    /^192\.168\.|^10\.|^172\.(1[6-9]|2\d|3[01])\./.test(hostname);
  if (localStation) return LIVE_ENDPOINT;
  return `${import.meta.env.BASE_URL}submit_student_wsi.php`;
}

export function buildEnvelope(
  schemaId: string,
  recordId: string,
  payload: unknown,
  contactPhone: string,
  identity: {
    contributor: ContributorBlock;
    device: DeviceBlock;
    completionArea: string;
    completionGps: string;
  },
): SubmissionEnvelope {
  return {
    schema_id: schemaId,
    record_id: recordId,
    submitted_at_client: new Date().toISOString(),
    contact_phone: contactPhone,
    app_version: "wp5",
    contributor: identity.contributor,
    device: identity.device,
    completion_area: identity.completionArea,
    completion_gps: identity.completionGps,
    payload,
    hp_field: "",
  };
}

export class SubmissionError extends Error {
  constructor(
    message: string,
    readonly kind: "network" | "rejected" | "server",
  ) {
    super(message);
  }
}

export async function submitEnvelope(
  envelope: SubmissionEnvelope,
): Promise<SubmissionReceipt> {
  let response: Response;
  try {
    response = await fetch(submissionEndpoint(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(envelope),
    });
  } catch {
    throw new SubmissionError(
      "The project server could not be reached. Your record stays saved on this device — try again when online.",
      "network",
    );
  }
  let body: { ok?: boolean; message?: string; receipt?: SubmissionReceipt };
  try {
    body = (await response.json()) as typeof body;
  } catch {
    throw new SubmissionError(
      "The server reply could not be read. Your record stays saved on this device.",
      "server",
    );
  }
  if (!response.ok || !body.ok || !body.receipt) {
    throw new SubmissionError(
      body.message ??
        "The server declined the submission. Your record stays saved on this device.",
      response.status === 422 || response.status === 400
        ? "rejected"
        : "server",
    );
  }
  return body.receipt;
}

// ---- payload builders (keep the phone OUT of every payload: it lives only
// in the envelope, the server file and the notification email — WP4 privacy
// rule; it never reaches the public GeoJSON or exports) ----

export function a2Payload(record: A2Record) {
  const { evidence, ...rest } = record;
  return {
    ...rest,
    evidence: evidence.map(({ blob: _blob, ...item }) => item),
  };
}

export function a1Payload(draft: A1Draft) {
  return draft;
}

export function a3oPayload(record: A3OfficialDraft) {
  return record;
}

export function a3cPayload(record: A3CommunityDraft) {
  return record;
}

export interface PendingItem {
  key: string;
  schemaId: string;
  recordId: string;
  label: string;
  detail: string;
  /** Owner decision 2026-08-04: the completion area is read from the
   *  record itself — the software fills the CSV; the contributor never
   *  types it separately. */
  completionArea: string;
  payload: unknown;
}

export function pendingFromStage2(state: Stage2State): PendingItem[] {
  const items: PendingItem[] = [];
  for (const asset of state.assets) {
    if (asset.syncState === "synced") continue;
    if (asset.captureDisposition === "enrich-later") continue;
    items.push({
      key: `a2:${asset.assetId}`,
      schemaId: SUBMISSION_SCHEMAS.A2,
      recordId: asset.assetId,
      label: asset.assetName || "Unnamed asset",
      detail: `A2 · ${asset.assetType || "type pending"} · ${asset.settlementArea || asset.municipalityCode || "area not set"}`,
      completionArea: asset.settlementArea || asset.municipalityCode || "",
      payload: a2Payload(asset),
    });
  }
  for (const record of state.a3OfficialRecords) {
    if (record.serverReceiptAt) continue;
    items.push({
      key: `a3o:${record.response.responseId}`,
      schemaId: SUBMISSION_SCHEMAS.A3O,
      recordId: record.response.responseId,
      label: `Official assessment · ${record.response.serviceArea || "area not set"}`,
      detail: `A3O · completed ${new Date(record.completedAt).toLocaleDateString("en-ZA")}`,
      completionArea: record.response.serviceArea || "",
      payload: a3oPayload(record.response),
    });
  }
  for (const record of state.a3CommunityRecords) {
    if (record.serverReceiptAt) continue;
    items.push({
      key: `a3c:${record.response.responseId}`,
      schemaId: SUBMISSION_SCHEMAS.A3C,
      recordId: record.response.responseId,
      label: `Community response · ${record.response.areaCode || "area not set"}`,
      detail: "A3C · community response, area-level",
      completionArea: record.response.areaCode || "",
      payload: a3cPayload(record.response),
    });
  }
  return items;
}

export function a1PendingItem(draft: A1Draft | null): PendingItem | null {
  if (!draft?.receipt) return null;
  const sentKey = `wsi-sent-a1-${draft.submissionId}`;
  if (localStorage.getItem(sentKey)) return null;
  return {
    key: `a1:${draft.submissionId}`,
    schemaId: SUBMISSION_SCHEMAS.A1,
    recordId: draft.submissionId,
    label: "Indicator weighting response",
    detail: `A1 · completed ${new Date(draft.receipt.completedAt).toLocaleDateString("en-ZA")} · reference ${draft.receipt.reference}`,
    completionArea:
      draft.profile.geographicExperienceDetail ||
      draft.profile.municipalityCode ||
      "",
    payload: a1Payload(draft),
  };
}

export function markA1Sent(draft: A1Draft) {
  localStorage.setItem(`wsi-sent-a1-${draft.submissionId}`, new Date().toISOString());
}
